/**
 * The Science.co model, day by day, so a month in progress can be judged
 * against where the plan says it should be by now rather than against the
 * whole month.
 *
 * NEW BUSINESS — spend, new customers, new revenue — follows the month's daily
 * ramp (see rampWeights). CAC and prices are constant within a model month, so
 * one weight vector serves all three.
 *
 * RENEWALS land on the day their cohort bought. A customer acquired on the
 * 12th of September renews on the 12th of whichever month the retention curve
 * says, so a month's renewals take the SHAPE of the months that sold them
 * rather than this month's ramp. A purchase on the 31st renews on the last day
 * of a shorter month.
 *
 * Every monthly total is preserved exactly: the days of a month sum to that
 * month's row in runModel.
 */

import { PLANS } from '../config/scienceModel'
import { daysInMonth, rampWeights, plannedSpend, shareToDate } from './scienceModel'

const pad = (n) => String(n).padStart(2, '0')

/**
 * @param {object}   a     assumptions (with levers already applied)
 * @param {object[]} plan  runModel(a)
 * @returns {{ month: string, weights: number[], days: object[] }[]} one per model month
 */
export function dailyPlan(a, plan) {
  const weights = rampWeights(plannedSpend(a), a.startMonth)

  return plan.map((row, m) => {
    const length = daysInMonth(row.month)
    const w = weights[m]
    const days = w.map((share, i) => ({
      date: `${row.month}-${pad(i + 1)}`,
      day: i + 1,
      spend: row.spend * share,
      newCustomers: row.newCustomers * share,
      newRevenue: row.newRevenue * share,
      recurringCustomers: 0,
      recurringRevenue: 0,
    }))

    for (let k = 0; k < m; k++) {
      const age = m - k
      const cohortWeights = weights[k]
      for (const planKey of PLANS) {
        const payers = (plan[k].newByPlan[planKey] || 0) * (a.retention[planKey]?.[age] ?? 0)
        if (!payers) continue
        const price = row.priceByPlan[planKey]
        cohortWeights.forEach((share, j) => {
          const day = days[Math.min(j, length - 1)]
          day.recurringCustomers += payers * share
          day.recurringRevenue += payers * share * price
        })
      }
    }

    for (const day of days) day.revenue = day.newRevenue + day.recurringRevenue
    return { month: row.month, weights: w, days }
  })
}

/** How each daily metric is read off the Daily Report's row. */
export const DAILY_METRICS = {
  spend: { label: 'Ad spend', format: 'money', actual: d => d.ad_spend },
  newCustomers: { label: 'New customers', format: 'count', actual: d => d.new_customers },
  revenue: { label: 'Revenue', format: 'money', actual: d => d.total_revenue },
  newRevenue: { label: 'New-customer revenue', format: 'money', actual: d => d.new_revenue },
  recurringRevenue: {
    label: 'Recurring revenue',
    format: 'money',
    actual: d => (d.recurring_revenue == null ? null : d.recurring_revenue + (d.unattributed_revenue || 0)),
  },
}

/**
 * One month, day by day, for one metric: daily plan and actual, the running
 * totals of both, and a projection from the latest actual to month end.
 *
 * The projection scales the plan's remaining ramp by how far actuals are from
 * plan-to-date — which is exactly how runModel paces the month — so the
 * month-end figure here and the paced monthly figure are the same number.
 *
 * @param {object} monthPlan  one entry from dailyPlan
 * @param {object|null} actual  the /science-model/actuals/daily payload for that month
 * @param {string} metric       a DAILY_METRICS key
 */
export function monthToDate(monthPlan, actual, metric) {
  const read = DAILY_METRICS[metric].actual
  const elapsed = actual ? Math.min(actual.days_elapsed || 0, monthPlan.days.length) : 0
  const actualByDate = new Map((actual?.days || []).map(d => [d.date, d]))

  // Plan to "now", counting the part of today that has passed.
  const planTotal = monthPlan.days.reduce((sum, d) => sum + d[metric], 0)
  const share = shareToDate(monthPlan.weights, elapsed)

  let cumPlan = 0
  let cumActual = 0
  let actualToDate = 0
  const lastActualDay = Math.ceil(elapsed)
  const points = monthPlan.days.map((planDay) => {
    cumPlan += planDay[metric]
    const row = actualByDate.get(planDay.date)
    const happened = planDay.day <= lastActualDay
    const dailyActual = happened ? (read(row || {}) ?? 0) : null
    if (happened) {
      cumActual += dailyActual
      actualToDate = cumActual
    }
    return {
      date: planDay.date,
      day: planDay.day,
      label: String(planDay.day),
      plan: planDay[metric],
      actual: dailyActual,
      cumPlan,
      cumActual: happened ? cumActual : null,
      partial: happened && planDay.day === lastActualDay && elapsed < lastActualDay,
    }
  })

  // Projection only once something has happened and there is month left.
  const pace = share > 0 ? actualToDate / share : null
  for (const point of points) {
    point.projection = pace != null && point.day >= lastActualDay
      ? pace * shareToDate(monthPlan.weights, point.day)
      : null
  }

  return {
    points,
    elapsed,
    planTotal,
    planToDate: planTotal * share,
    actualToDate: actual ? actualToDate : null,
    projection: pace,
    complete: actual ? elapsed >= monthPlan.days.length : false,
  }
}

/** 'YYYY-MM-DD' plus n days. */
export function addDays(date, n) {
  // eslint-disable-next-line no-restricted-syntax -- UTC calendar arithmetic, no zone involved
  return new Date(Date.parse(`${date}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10)
}

/** The Monday of a date's ISO week, as the Daily Report keys weeks. */
export function mondayOf(date) {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay() // 0 = Sunday
  return addDays(date, -((day + 6) % 7))
}

/** Every day of every model month, flattened, keyed by date. */
function daysByDate(daily) {
  const map = new Map()
  for (const month of daily) for (const day of month.days) map.set(day.date, day)
  return map
}

export const TRAILING_DAYS = 7

/**
 * How far to scale the plan so it starts from the spend we actually have.
 *
 * The ramped plan keeps the model's growth rates and changes only its level:
 * every month's spend, and so its customers and revenue through CAC, times one
 * factor. The factor is chosen so the ramped plan's spend over the last seven
 * FULL days equals what was actually spent over them. Seven days, because one
 * day swings (Sep 16 was $6.7K, Sep 12 $0.9K); full days, because today's
 * spend is still landing. Comparing against the plan's own days rather than a
 * month average keeps the within-month ramp out of the answer.
 *
 * Days outside the model's horizon are left out of both sides. Null when no
 * day in the window is inside it, or the plan spends nothing there.
 *
 * @param {object[]} daily        dailyPlan() of the unramped assumptions
 * @param {object[]} actualDays   Daily Report rows (ad_spend in dollars)
 * @param {string}   asOf         today, 'YYYY-MM-DD', Eastern
 */
export function rampFactor(daily, actualDays, asOf) {
  const planDays = daysByDate(daily)
  const actualByDate = new Map((actualDays || []).map(d => [d.date, d]))
  let planSpend = 0
  let actualSpend = 0
  const dates = []
  for (let i = TRAILING_DAYS; i >= 1; i--) {
    const date = addDays(asOf, -i)
    const planDay = planDays.get(date)
    if (!planDay) continue
    dates.push(date)
    planSpend += planDay.spend
    actualSpend += actualByDate.get(date)?.ad_spend || 0
  }
  if (!dates.length || planSpend <= 0) return null
  return {
    factor: actualSpend / planSpend,
    from: dates[0],
    to: dates[dates.length - 1],
    days: dates.length,
    actualSpend,
    planSpend,
  }
}

/** The assumptions with spend, and so everything downstream of it, scaled. */
export function rampedAssumptions(a, factor) {
  return { ...a, acquisition: { ...a.acquisition, startingSpend: a.acquisition.startingSpend * factor } }
}

/** A plan day's figure for a metric, whichever daily entry it came from. */
function dayValue(day, metric) {
  return day ? day[metric] : 0
}

/**
 * The horizon in ISO weeks (Monday to Sunday), with plan, ramped plan and
 * actual per week, and — for the week in progress — both plans to date.
 *
 * A week straddling the model's start holds only its in-horizon days, on every
 * side, so the three are always measured over the same days.
 *
 * @param {object[]} daily        dailyPlan() of the plan
 * @param {object[]|null} rampedDaily  dailyPlan() of the ramped plan, or null
 * @param {object|null} actual    /actuals/days payload
 * @param {string} metric         a DAILY_METRICS key
 */
export function weeklySeries(daily, rampedDaily, actual, metric) {
  const read = DAILY_METRICS[metric].actual
  const planDays = daysByDate(daily)
  const rampedDays = rampedDaily ? daysByDate(rampedDaily) : null
  const actualByDate = new Map((actual?.days || []).map(d => [d.date, d]))
  const asOf = actual?.asOf || null
  const todayFraction = actual?.today_fraction ?? 1

  const weeks = new Map()
  for (const [date, planDay] of planDays) {
    const key = mondayOf(date)
    const week = weeks.get(key) || {
      week: key, plan: 0, ramped: rampedDays ? 0 : null,
      actual: null, planToDate: 0, rampedToDate: rampedDays ? 0 : null, days: 0, daysHappened: 0,
    }
    week.days += 1
    const rampedDay = rampedDays?.get(date)
    week.plan += dayValue(planDay, metric)
    if (rampedDays) week.ramped += dayValue(rampedDay, metric)

    // How much of this day has happened: all of a past day, part of today.
    const happened = !asOf ? 0 : date < asOf ? 1 : date === asOf ? todayFraction : 0
    if (happened > 0) {
      week.daysHappened += happened
      week.planToDate += dayValue(planDay, metric) * happened
      if (rampedDays) week.rampedToDate += dayValue(rampedDay, metric) * happened
      week.actual = (week.actual || 0) + (read(actualByDate.get(date) || {}) ?? 0)
    }
    weeks.set(key, week)
  }

  return [...weeks.values()]
    .sort((x, y) => (x.week < y.week ? -1 : 1))
    .map(week => ({
      ...week,
      complete: week.daysHappened >= week.days,
      partial: week.daysHappened > 0 && week.daysHappened < week.days,
    }))
}
